import { blacklistProdInstanceIds } from '@/constants/instances';
import { environment } from '@/environment';
import { Instance, Language } from '@/models';
import { AuthService, ConfigService, ErrorService, FileService, LanguageService } from '@/services';
import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { SpinnerButtonConfig } from '@/ui/spinner-button/spinner-button-config';
import { MaintenanceConfig } from 'src/app/models/maintenanceConfig';
import { FirstLoginDialogComponent, VoucherActiveDialogComponent, VoucherDialogComponent } from '../../components';
import { buildSsoState } from '../../utils/build-sso-state';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit {
  schools: Instance[] = [];
  environments: Instance[] = [];
  languages: Language[] = [];
  localStoredLanguage: Language | undefined;
  emailFormControl = new UntypedFormControl('', [Validators.required, Validators.email]);
  schoolSelectFormControl = new UntypedFormControl('', [Validators.required]);
  showSchoolList: boolean = false;
  lockedInstance: Instance | null = null;

  languageSelectFormControl = new UntypedFormControl('');
  showLanguageList: boolean = true;
  storedLanguage: string = 'nl-NL';

  maintenanceTextNL: string = '';
  maintenanceTextEN: string = '';
  preventLogin: boolean = false;

  btnConfig: SpinnerButtonConfig = {
    text: `Inloggen`,
  };

  schoolSelectedBtnConfig: SpinnerButtonConfig = {
    text: `Inloggen`,
  };

  constructor(
    private route: ActivatedRoute,
    private authService: AuthService,
    private router: Router,
    private errorService: ErrorService,
    public dialog: MatDialog,
    private configService: ConfigService,
    private languageService: LanguageService,
    private fileService: FileService
  ) {}

  ngOnInit(): void {
    this.languageService.setLocale().then(() => {
      this.setButtonTexts();
    });

    this.route.url
      .subscribe(url => {
        var urlObj: any = url as any;
        if (urlObj[0] && urlObj[0].path == 'uitloggen') {
          this.authService.logout();
        }
      })
      .unsubscribe();

    this.isMaintenanceActive().then(() => {
      if (this.preventLogin && environment.production) {
        this.emailFormControl.disable();
        this.btnConfig.disabled = true;
        this.schoolSelectedBtnConfig.disabled = true;
      } else {
        this.loadData();
      }
    });
  }

  private loadData() {
    this.route.queryParams
      .subscribe(params => {
        if (params['token'] != undefined) {
          this.authService.storeToken(params['token']);
          if (params['first'] != undefined && params['first'] === 'true') {
            const dialogRef = this.dialog.open(FirstLoginDialogComponent, {
              width: '40vw',
              minWidth: '400px',
              disableClose: true,
            });
            dialogRef.afterClosed().subscribe(resp => {
              this.router.navigateByUrl('');
            });
          } else {
            this.router.navigateByUrl('');
            return;
          }
        }
        if (params['voucher_request_id'] != undefined && params['tenant'] != undefined) {
          this.dialog.open(VoucherDialogComponent, {
            width: '40vw',
            minWidth: '200px',
            disableClose: true,
            data: {
              userId: params['voucher_request_id'],
              tenant: params['tenant'],
              first: params['first'],
            },
          });
        }
        if (params['voucher_code'] != undefined && params['voucher_active'] != undefined) {
          this.dialog.open(VoucherActiveDialogComponent, {
            width: '40vw',
            minWidth: '200px',
            disableClose: true,
            data: {
              code: params['voucher_code'],
              active: params['voucher_active'],
            },
          });
        }
      })
      .unsubscribe();
    this.configService.getInstitutes().subscribe(
      resp => {
        this.schools = resp as Instance[];
        for (let school of this.schools) {
          if (environment.production) {
            if (!blacklistProdInstanceIds.includes(school.id)) {
              this.environments.push(school);
            }
          } else {
            this.environments.push(school);
          }
        }
        this.environments.sort((a, b) => a.name.localeCompare(b.name));
        this.applySubdomainTenant();
      },
      err => this.errorService.onError(err)
    );
    this.languageService.getAvailableLanguages().subscribe(
      resp => {
        this.languages = resp as Language[];

        this.languageSelectFormControl.setValue(
          this.languages.find(x => x.locale === this.languageService.getLocale())
        );
      },
      err => this.errorService.onError(err)
    );
  }

  private isMaintenanceActive() {
    return new Promise<boolean>((resolve, reject) => {
      this.fileService.getMaintenanceFile().subscribe({
        next: (d: MaintenanceConfig) => {
          if (d && d.active && new Date(d.start) <= new Date() && new Date(d.end) >= new Date()) {
            this.maintenanceTextNL = d.messageNL;
            this.maintenanceTextEN = d.messageEN;
            this.preventLogin = d.preventLogin;
          }
          resolve(true);
        },
        error: err => {
          console.log('Error fetching maintenance file:', err);
          resolve(false);
        },
      });
    });
  }

  private setButtonTexts() {
    this.btnConfig.text = this.languageService.translateWithCap('login.login');
    this.schoolSelectedBtnConfig.text = this.languageService.translateWithCap('login.login');
  }

  onLangChange(event: any) {
    this.languageService.setLocale(event.value.locale).then(() => {
      this.setButtonTexts();
    });
  }

  private surfConextLogin(state: string, entityId: string) {
    window.location.href = this.authService.getSurfConextLoginUrl(state, entityId);
  }

  private entreeLogin(state: string, entityId: string) {
    window.location.href = this.authService.getEntreeLoginUrl(state, entityId);
  }

  private msLogin(state: string, entityId: string, email: string) {
    window.location.href = this.authService.getMsLoginUrl(state, entityId, email);
  }

  private googleLogin(state: string, entityId: string, email: string) {
    window.location.href = this.authService.getGoogleLoginUrl(state, entityId, email);
  }

  private getSubdomain(): string | null {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    // e.g. fontys.depd.online → ["fontys", "depd", "online"]
    if (parts.length > 2) {
      return parts[0];
    }
    return null;
  }

  private applySubdomainTenant() {
    if (environment.test || environment.develop) return;
    const params = this.route.snapshot.queryParams;
    if (params['token'] || params['voucher_request_id'] || params['voucher_code']) return;
    const subdomain = this.getSubdomain();
    if (!subdomain) return;
    const match = this.environments.find(
      s => (s.subdomain ?? s.id) === subdomain
    );
    if (match) {
      this.lockedInstance = match;
      if (!this.authService.isLoggedIn().login) {
        this.ssoRedirect(match);
      }
    }
  }

  onSsoLogin() {
    this.btnConfig.loading = true;
    if (!this.showSchoolList) {
      for (let school of this.schools) {
        for (let l of school.domains) {
          if ((this.emailFormControl.value as string).toLowerCase().endsWith(l.domain)) {
            this.ssoRedirect(school);
            this.btnConfig.loading = false;
            this.emailFormControl.reset();
            return;
          }
        }
      }
      this.showSchoolList = true;
      return;
    } else {
      for (let school of this.schools) {
        if (school.id == this.schoolSelectFormControl.value) {
          this.ssoRedirect(school);
          this.btnConfig.loading = false;
          this.emailFormControl.reset();
          this.schoolSelectFormControl.reset();
          return;
        }
      }
    }
  }

  private ssoRedirect(school: Instance) {
    const state = buildSsoState(school.id, window.location.origin);
    let email = (this.emailFormControl.value as string).toLowerCase();
    switch (school.provider) {
      case 'surf':
        this.surfConextLogin(state, school.entityId);
        break;
      case 'entree':
        this.entreeLogin(state, school.entityId);
        break;
      case 'ms':
        this.msLogin(state, school.entityId, email);
        break;
      case 'google':
        this.googleLogin(state, school.entityId, email);
        break;
      case '':
        this.router.navigateByUrl('/login/beheer?email=' + email + '&school=' + school.id);
        break;
    }
  }
}
